import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { fromProtoDate, isSameDay } from '~/models/dateTime'
import {
  type IntradayInversion,
  detectIntradayBidInversions,
  isIntradayInversionPoolState,
} from '~/models/intradayBidInversion'
import { convertIntradayBidsFromOrders } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'

/**
 * 1 つの受渡日の注文 (一覧 RPC の行) から売買逆転を検知する (AC-BIN-045)。 入札追加タブの
 * 検知バナーと承認確認の明細が、 検知対象プールの絞り込みをここで共有する。
 *
 * 検知単位は (受渡日, コマ, エリア) なので、 受渡日の一致は一覧 RPC のフィルタ任せにせず
 * ここでも確かめる (= 受渡日切替直後に前の受渡日の cache が残っていても、 別受渡日の
 * 買 / 売 を交差扱いしない。 一括作成の重複判定 `bulkExistingBids` の再フィルタと同じ担保)。
 * プールに入れるかは一覧 RPC の行の状態で判定する (表示用に写した行の承認軸は、 下書きと
 * 承認済を区別しない)。
 *
 * @param options.isBeforeGc GC 前の札か (GC を過ぎた札はプールに入れない)。
 * @param options.isSubmitTarget 今回提出する札か。 未提出の下書きにだけ効き、 提出する札だけを
 *   プールに入れる (AC-BIN-045。 提出しない下書きは含めない)。 板にある札・承認済の札の
 *   採否は、 この結果によらない。
 */
export const detectIntradayDeliveryDateInversions = (
  orders: readonly IntradayOrderRow[],
  targetDate: Date,
  {
    isBeforeGc,
    isSubmitTarget,
  }: {
    isBeforeGc: (order: IntradayOrderRow) => boolean
    isSubmitTarget: (order: IntradayOrderRow) => boolean
  },
): IntradayInversion[] =>
  detectIntradayBidInversions(
    convertIntradayBidsFromOrders(
      orders.filter(
        (o) =>
          o.targetDate !== undefined &&
          isSameDay(fromProtoDate(o.targetDate), targetDate) &&
          isBeforeGc(o) &&
          isIntradayInversionPoolState(o, {
            includeUnsubmittedDrafts: isSubmitTarget(o),
          }),
      ),
    ),
  )
